import { Button } from '@attive/ui/button'
import { Checkbox } from '@attive/ui/checkbox'
import { Input } from '@attive/ui/input'
import { Label } from '@attive/ui/label'
import { StatusButton } from '@attive/ui/status-button'
import {
	getFormProps,
	getInputProps,
	useForm,
	useInputControl,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import type { SEOHandle } from '@nasa-gcn/remix-seo'
import {
	type ActionFunctionArgs,
	data,
	Form,
	Link,
	type LoaderFunctionArgs,
	type MetaFunction,
	useActionData,
	useSearchParams,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { AuthPageLayout } from '#app/components/AuthPageLayout.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList } from '#app/components/forms.tsx'
import { GoogleIcon } from '#app/components/icons.tsx'
import { login, requireAnonymous } from '#app/utils/auth.server.ts'
import { providerNames } from '#app/utils/connections.tsx'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { EmailSchema, PasswordSchema } from '#app/utils/user-validation.ts'
import { handleNewSession } from './login.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const LoginFormSchema = z.object({
	email: EmailSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request)
	return data({})
}

export async function action({ request }: ActionFunctionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				const session = await login({
					email: data.email,
					password: data.password,
				})
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Invalid email or password',
					})
					return z.NEVER
				}

				return { ...data, session }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return data(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { session, remember, redirectTo } = submission.value

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

function RememberMeCheckbox({
	id,
	name,
	form,
	defaultChecked,
}: {
	id: string
	name: string
	form: string
	defaultChecked?: boolean
}) {
	const checkedValue = 'on'
	const input = useInputControl({
		key: id,
		name,
		formId: form,
		initialValue: defaultChecked ? checkedValue : undefined,
	})

	return (
		<div className="flex items-center gap-2">
			<Checkbox
				id={id}
				checked={input.value === checkedValue}
				onCheckedChange={(state) => {
					input.change(state.valueOf() ? checkedValue : undefined)
				}}
				onFocus={() => input.focus()}
				onBlur={() => input.blur()}
				className="size-4 rounded border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
			/>
			<Label
				htmlFor={id}
				className="text-sm font-normal text-foreground-subtle cursor-pointer"
			>
				Remember me
			</Label>
		</div>
	)
}

export default function LoginPage() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		// TODO: fix action data type
		lastResult: actionData?.result as any,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<AuthPageLayout
			title="Welcome back"
			description="You know the drill."
			footerText="New around here?"
			footerLinkText="Create an account"
			footerLinkTo={
				redirectTo
					? `/signup?redirectTo=${encodeURIComponent(redirectTo)}`
					: '/signup'
			}
		>
			<div className="flex flex-col gap-6">
				<Form
					method="POST"
					{...getFormProps(form)}
					className="flex flex-col gap-6"
				>
					<HoneypotInputs />

					{/* Email Field */}
					<div className="flex flex-col gap-1.5">
						<Label
							htmlFor={fields.email.id}
							className="text-sm text-foreground"
						>
							Email
						</Label>
						<Input
							{...getInputProps(fields.email, { type: 'email' })}
							autoFocus
							autoComplete="email"
							placeholder="you@company.com"
							className="h-10 rounded bg-background border-border placeholder:text-foreground-muted/50"
						/>
						{fields.email.errors && (
							<ErrorList
								errors={fields.email.errors}
								id={`${fields.email.id}-error`}
							/>
						)}
					</div>

					{/* Password Field */}
					<div className="flex flex-col gap-1.5">
						<Label
							htmlFor={fields.password.id}
							className="text-sm text-foreground"
						>
							Password
						</Label>
						<Input
							{...getInputProps(fields.password, { type: 'password' })}
							autoComplete="current-password"
							placeholder="Your secret handshake"
							className="h-10 rounded bg-background border-border placeholder:text-foreground-muted/50"
						/>
						{fields.password.errors && (
							<ErrorList
								errors={fields.password.errors}
								id={`${fields.password.id}-error`}
							/>
						)}
					</div>

					{/* Remember me and Forgot password */}
					<div className="flex items-center justify-between">
						<RememberMeCheckbox
							id={fields.remember.id}
							name={fields.remember.name}
							form={form.id}
						/>
						<Link
							to="/forgot-password"
							className="text-sm font-medium text-foreground-subtle hover:text-foreground"
						>
							Brain fog? Reset it
						</Link>
					</div>

					<input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />
					<ErrorList errors={form.errors} id={form.errorId} />

					{/* Submit Button */}
					<StatusButton
						className="w-full h-10"
						status={isPending ? 'pending' : (form.status ?? 'idle')}
						type="submit"
						disabled={isPending}
					>
						Let me in
					</StatusButton>
				</Form>

				{/* OAuth Buttons - must be outside the main form */}
				<div className="flex flex-col gap-4">
					{providerNames.map((providerName) => (
						<Form
							key={providerName}
							action={`/auth/${providerName}`}
							method="POST"
						>
							{redirectTo && (
								<input type="hidden" name="redirectTo" value={redirectTo} />
							)}
							<Button
								type="submit"
								variant="secondary"
								className="w-full h-10 gap-2.5"
							>
								<GoogleIcon />
								<span>login with google</span>
							</Button>
						</Form>
					))}
				</div>
			</div>
		</AuthPageLayout>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Login to Attive' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
